/**
 * QR Code Management Component
 * Generate, view, print QR codes and view scan analytics
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  QrCode,
  Plus,
  Download,
  Printer,
  Trash2,
  BarChart3,
  Users,
  UserPlus,
  Calendar,
  Loader2,
  Copy,
  Check,
  ExternalLink
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface QRCodeItem {
  id: string;
  organization_id?: number;
  qr_type: string; // Backend uses qr_type
  name?: string;
  description?: string;
  event_name?: string; // Per-event label for contact_signup / event_checkin types
  qr_token: string; // Backend uses qr_token
  target_url: string;
  workflow_id?: number;
  expires_at?: string;
  max_scans?: number;
  current_scans: number; // Backend uses current_scans
  is_active: boolean;
  created_at?: string;
  qr_image?: string; // Backend uses qr_image for Base64 encoded QR code
  owner_type?: string;
  owner_id?: string;
  /** public_form codes: which public link the code opens right now. */
  qr_data?: { public_form_token_id?: number; lang?: string } | null;
}

/** A public form link a `public_form` QR code can point at (TRUS-624). */
interface PublicLinkOption {
  id: number;
  title?: string | null;
  template_name: string;
  is_active: boolean;
}

interface ScanAnalytics {
  total_scans: number;
  unique_devices: number;
  scans_by_date: Array<{ date: string; count: number }>;
  recent_scans: Array<{
    scanned_at: string;
    device_type?: string;
    location?: string;
  }>;
}

interface QRCodeManagementProps {
  organizationId: number;
}

export function QRCodeManagement({ organizationId }: QRCodeManagementProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [qrCodes, setQrCodes] = useState<QRCodeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedQR, setSelectedQR] = useState<QRCodeItem | null>(null);
  const [analytics, setAnalytics] = useState<ScanAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [publicLinks, setPublicLinks] = useState<PublicLinkOption[]>([]);

  // Create form state
  const [newQR, setNewQR] = useState({
    code_type: 'member_onboarding' as string,
    name: '',
    description: '',
    event_name: '',
    max_scans: 0,
    expires_days: 0,
    // Action configuration
    auto_add_member: false,
    send_email_on_scan: false,
    send_sms_on_scan: false,
    // public_form: the link to open, and the language to open it in
    public_form_token_id: 0,
    lang: '',
  });

  useEffect(() => {
    fetchQRCodes();
    fetchPublicLinks();
  }, [organizationId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(cboId && { 'X-CBO-ID': cboId }),
    };
  };

  // Public form links this org can point a QR code at.
  const fetchPublicLinks = async () => {
    try {
      const response = await fetch(getWeaveinApiUrl('/api/forms/public-tokens'), { headers: getAuthHeaders() });
      if (response.ok) setPublicLinks(await response.json());
    } catch {
      setPublicLinks([]);
    }
  };

  // Send an already-printed public-form QR code somewhere else. The code itself
  // never changes — it is looked up at each scan — so nothing is reprinted.
  const retargetQRCode = async (qrCode: QRCodeItem, publicFormTokenId: number) => {
    setError(null);
    try {
      const response = await fetch(getWeaveinApiUrl(`/api/qr-codes/${qrCode.id}/target`), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ public_form_token_id: publicFormTokenId, lang: qrCode.qr_data?.lang || undefined }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || t('settings.qr_retarget_failed', 'Could not change where this QR code goes'));
        return;
      }
      setQrCodes((prev) =>
        prev.map((q) =>
          q.id === qrCode.id ? { ...q, qr_data: { ...(q.qr_data || {}), public_form_token_id: publicFormTokenId } } : q,
        ),
      );
    } catch {
      setError(t('settings.qr_retarget_failed', 'Could not change where this QR code goes'));
    }
  };

  const publicLinkLabel = (link: PublicLinkOption) =>
    `${link.title || link.template_name}${link.is_active ? '' : ` (${t('settings.inactive', 'inactive')})`}`;

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        getWeaveinApiUrl(`/api/qr-codes?organization_id=${organizationId}&is_active=true`),
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setQrCodes(data);
      } else {
        // If no QR codes yet, just set empty array
        setQrCodes([]);
      }
    } catch (err) {
      console.error('Error fetching QR codes:', err);
      setQrCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const createQRCode = async () => {
    try {
      setCreating(true);
      setError(null);

      const response = await fetch(
        getWeaveinApiUrl('/api/qr-codes'),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            organization_id: organizationId,
            qr_type: newQR.code_type,
            owner_type: 'organization',
            owner_id: String(organizationId),
            name: newQR.name,
            description: newQR.description || undefined,
            event_name: (newQR.code_type === 'contact_signup' || newQR.code_type === 'event_checkin')
              ? (newQR.event_name || undefined)
              : undefined,
            max_scans: newQR.max_scans || undefined,
            public_form_token_id: newQR.code_type === 'public_form' ? newQR.public_form_token_id || undefined : undefined,
            lang: newQR.code_type === 'public_form' ? newQR.lang || undefined : undefined,
            // Action configuration
            auto_add_member: newQR.auto_add_member,
            send_email_on_scan: newQR.send_email_on_scan,
            send_sms_on_scan: newQR.send_sms_on_scan
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Map create response to QRCodeItem format
        const newQRCode: QRCodeItem = {
          id: data.qr_code_id,
          qr_type: newQR.code_type,
          name: newQR.name,
          description: newQR.description || undefined,
          event_name: newQR.event_name || undefined,
          qr_token: data.qr_token,
          target_url: data.target_url,
          qr_image: data.qr_image,
          max_scans: newQR.max_scans || undefined,
          current_scans: 0,
          is_active: true,
          created_at: new Date().toISOString(),
          owner_type: 'organization',
          owner_id: String(organizationId),
          qr_data: newQR.code_type === 'public_form'
            ? { public_form_token_id: newQR.public_form_token_id, lang: newQR.lang || undefined }
            : null,
        };
        setQrCodes(prev => [newQRCode, ...prev]);
        setShowCreateModal(false);
        setNewQR({
          code_type: 'member_onboarding',
          name: '',
          description: '',
          event_name: '',
          max_scans: 0,
          expires_days: 0,
          auto_add_member: false,
          send_email_on_scan: false,
          send_sms_on_scan: false,
          public_form_token_id: 0,
          lang: '',
        });
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create QR code');
      }
    } catch (err) {
      setError('Error creating QR code');
      console.error('Error creating QR code:', err);
    } finally {
      setCreating(false);
    }
  };

  const fetchAnalytics = async (qrCodeId: string) => {
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/qr-codes/${qrCodeId}/analytics`),
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  };

  const deleteQRCode = async (qrCodeId: string) => {
    if (!confirm('Are you sure you want to delete this QR code? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/qr-codes/${qrCodeId}`),
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        setQrCodes(prev => prev.filter(qr => qr.id !== qrCodeId));
        if (selectedQR?.id === qrCodeId) {
          setSelectedQR(null);
          setAnalytics(null);
        }
      }
    } catch (err) {
      console.error('Error deleting QR code:', err);
    }
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadQRCode = (qrCode: QRCodeItem) => {
    if (!qrCode.qr_image) return;

    const link = document.createElement('a');
    link.href = qrCode.qr_image;
    link.download = `${(qrCode.name || 'qr_code').replace(/\s+/g, '_')}_qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printQRCode = (qrCode: QRCodeItem) => {
    if (!qrCode.qr_image) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${qrCode.name || 'QR Code'} - QR Code</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                margin: 0;
                font-family: sans-serif;
              }
              img { max-width: 400px; }
              h1 { margin-bottom: 10px; }
              p { color: #666; margin-top: 0; }
            </style>
          </head>
          <body>
            <h1>${qrCode.name || 'QR Code'}</h1>
            ${qrCode.description ? `<p>${qrCode.description}</p>` : ''}
            <img src="${qrCode.qr_image}" alt="QR Code" />
            <p style="margin-top: 20px; font-size: 12px;">{t('settings.scan_to_get_started', 'Scan to get started')}</p>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getCodeTypeLabel = (type: string) => {
    switch (type) {
      case 'member_onboarding':
        return 'Member Onboarding';
      case 'user_invitation':
        return 'User Invitation';
      case 'program_enrollment':
        return 'Program Enrollment';
      case 'event_checkin':
        return 'Event Check-in';
      case 'contact_signup':
        return 'Contact Sign-up';
      case 'public_form':
        return t('settings.public_form_survey', 'Public Form / Survey');
      default:
        return type || 'Custom';
    }
  };

  const getCodeTypeIcon = (type: string) => {
    switch (type) {
      case 'member_onboarding':
        return <Users className="h-4 w-4" />;
      case 'user_invitation':
        return <UserPlus className="h-4 w-4" />;
      case 'program_enrollment':
        return <Calendar className="h-4 w-4" />;
      case 'event_checkin':
        return <BarChart3 className="h-4 w-4" />;
      case 'contact_signup':
        return <UserPlus className="h-4 w-4" />;
      case 'public_form':
        return <ExternalLink className="h-4 w-4" />;
      default:
        return <QrCode className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t('settings.qr_code_management', 'QR Code Management')}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t('settings.generate_qr_codes_for_member', 'Generate QR codes for member onboarding and track scans')}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          {t('settings.create_qr_code', 'Create QR Code')}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* QR Codes List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : qrCodes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <QrCode className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h4 className="font-medium text-gray-900 dark:text-white">{t('settings.no_qr_codes_yet', 'No QR codes yet')}</h4>
          <p className="text-sm text-gray-500 mt-1">
            {t('settings.create_your_first_qr_code', 'Create your first QR code for member onboarding')}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 text-blue-600 hover:text-blue-700 text-sm"
          >
            + Create QR Code
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {qrCodes.map((qrCode) => (
            <div
              key={qrCode.id}
              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                selectedQR?.id === qrCode.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'hover:border-gray-300'
              }`}
              onClick={() => {
                setSelectedQR(qrCode);
                fetchAnalytics(qrCode.id);
              }}
            >
              {/* QR Code Preview */}
              {qrCode.qr_image && (
                <div className="flex justify-center mb-3">
                  <img
                    src={qrCode.qr_image}
                    alt={qrCode.name || 'QR Code'}
                    className="w-32 h-32"
                  />
                </div>
              )}

              {/* Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {getCodeTypeIcon(qrCode.qr_type)}
                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                    {getCodeTypeLabel(qrCode.qr_type)}
                  </span>
                </div>
                <h4 className="font-medium">{qrCode.name || 'Unnamed QR Code'}</h4>
                {qrCode.event_name && (
                  <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                    {t('settings.event', 'Event')}: {qrCode.event_name}
                  </p>
                )}
                {qrCode.description && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{qrCode.description}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <BarChart3 className="h-3 w-3" />
                    {qrCode.current_scans} scans
                  </span>
                  {qrCode.expires_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Expires {fmt.short(qrCode.expires_at)}
                    </span>
                  )}
                </div>
              </div>

              {/* Destination — a public-form code can be re-pointed after printing */}
              {qrCode.qr_type === 'public_form' && (
                <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {t('settings.qr_opens', 'Opens')}
                  </label>
                  <select
                    value={qrCode.qr_data?.public_form_token_id || ''}
                    onChange={(e) => retargetQRCode(qrCode, Number(e.target.value))}
                    className="w-full px-2 py-1.5 text-sm border rounded-md"
                  >
                    {!qrCode.qr_data?.public_form_token_id && <option value="">—</option>}
                    {publicLinks.map((link) => (
                      <option key={link.id} value={link.id}>{publicLinkLabel(link)}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(qrCode.target_url, qrCode.id);
                  }}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                  title={t('settings.copy_url', 'Copy URL')}
                >
                  {copied === qrCode.id ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadQRCode(qrCode);
                  }}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                  title={t('settings.download', 'Download')}
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    printQRCode(qrCode);
                  }}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                  title={t('settings.print', 'Print')}
                >
                  <Printer className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteQRCode(qrCode.id);
                  }}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded ml-auto"
                  title={t('settings.delete', 'Delete')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Analytics Panel */}
      {selectedQR && analytics && (
        <div className="bg-white dark:bg-gray-800 border rounded-lg p-6">
          <h4 className="font-medium mb-4">
            Analytics: {selectedQR.name}
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold">{analytics.total_scans}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">{t('settings.total_scans', 'Total Scans')}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold">{analytics.unique_devices}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">{t('settings.unique_devices', 'Unique Devices')}</div>
            </div>
          </div>

          {analytics.recent_scans.length > 0 && (
            <div className="mt-4">
              <h5 className="text-sm font-medium mb-2">{t('settings.recent_scans', 'Recent Scans')}</h5>
              <div className="space-y-2">
                {analytics.recent_scans.slice(0, 5).map((scan, index) => (
                  <div
                    key={index}
                    className="text-xs text-gray-600 flex items-center justify-between"
                  >
                    <span>{fmt.dateTime(scan.scanned_at)}</span>
                    {scan.device_type && (
                      <span className="bg-gray-100 px-2 py-0.5 rounded">
                        {scan.device_type}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Modal - Portal to render outside parent modal's DOM */}
      {showCreateModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[90vh] flex flex-col">
            {/* Fixed Header */}
            <div className="p-6 pb-4 border-b">
              <h3 className="text-lg font-semibold">{t('settings.create_qr_code_1', 'Create QR Code')}</h3>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 pt-4 overflow-y-auto flex-1 space-y-4">
              {/* Code Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.qr_code_type', 'QR Code Type')}
                </label>
                <select
                  value={newQR.code_type}
                  onChange={(e) => setNewQR(prev => ({
                    ...prev,
                    code_type: e.target.value,
                    event_name: ''
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="member_onboarding">{t('settings.member_onboarding', 'Member Onboarding')}</option>
                  <option value="user_invitation">{t('settings.user_invitation', 'User Invitation')}</option>
                  <option value="program_enrollment">{t('settings.program_enrollment', 'Program Enrollment')}</option>
                  <option value="event_checkin">{t('settings.event_checkin', 'Event Check-in')}</option>
                  <option value="contact_signup">{t('settings.contact_signup', 'Contact Sign-up (Tabling/Events)')}</option>
                  <option value="public_form">{t('settings.public_form_survey', 'Public Form / Survey')}</option>
                </select>
              </div>

              {/* Public form — which link the code opens. Changeable later
                  without reprinting; no location is recorded on scan. */}
              {newQR.code_type === 'public_form' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.public_form_link', 'Public form link *')}
                    </label>
                    <select
                      value={newQR.public_form_token_id || ''}
                      onChange={(e) => setNewQR(prev => ({ ...prev, public_form_token_id: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t('settings.choose_link', 'Choose a link…')}</option>
                      {publicLinks.filter((l) => l.is_active).map((link) => (
                        <option key={link.id} value={link.id}>{publicLinkLabel(link)}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('settings.public_form_qr_help', 'You can change which link this code opens at any time, without reprinting it.')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('settings.open_in_language', 'Open in language')}
                    </label>
                    <select
                      value={newQR.lang}
                      onChange={(e) => setNewQR(prev => ({ ...prev, lang: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t('settings.language_choice_on_page', 'Visitor chooses (English first)')}</option>
                      <option value="en">English</option>
                      <option value="es">Español</option>
                    </select>
                  </div>
                </>
              )}

              {/* Event Name — shown for contact_signup and event_checkin types */}
              {(newQR.code_type === 'contact_signup' || newQR.code_type === 'event_checkin') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.event_name', 'Event Name')}
                    <span className="ml-1 text-xs text-gray-500">({t('settings.associates_signups_with_event', 'associates sign-ups with this event')})</span>
                  </label>
                  <input
                    type="text"
                    value={newQR.event_name}
                    onChange={(e) => setNewQR(prev => ({ ...prev, event_name: e.target.value }))}
                    placeholder="e.g., Cat Rally 2026, Farmworker Tabling July"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.name', 'Name *')}
                </label>
                <input
                  type="text"
                  value={newQR.name}
                  onChange={(e) => setNewQR(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Lobby Poster QR Code"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.description', 'Description')}
                </label>
                <textarea
                  value={newQR.description}
                  onChange={(e) => setNewQR(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('settings.optional_description', 'Optional description')}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Max Scans */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Scans (0 = unlimited)
                </label>
                <input
                  type="number"
                  value={newQR.max_scans}
                  onChange={(e) => setNewQR(prev => ({
                    ...prev,
                    max_scans: parseInt(e.target.value) || 0
                  }))}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Expiry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expires in (days, 0 = never)
                </label>
                <input
                  type="number"
                  value={newQR.expires_days}
                  onChange={(e) => setNewQR(prev => ({
                    ...prev,
                    expires_days: parseInt(e.target.value) || 0
                  }))}
                  min="0"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Action Configuration — not for a public form, which only opens a link */}
              {newQR.code_type !== 'public_form' && (
              <div className="border-t pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('settings.actions_on_scan', 'Actions on Scan')}
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newQR.auto_add_member}
                      onChange={(e) => setNewQR(prev => ({
                        ...prev,
                        auto_add_member: e.target.checked
                      }))}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.autoadd_as_member', 'Auto-add as member')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newQR.send_email_on_scan}
                      onChange={(e) => setNewQR(prev => ({
                        ...prev,
                        send_email_on_scan: e.target.checked
                      }))}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.send_email_notification', 'Send email notification')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newQR.send_sms_on_scan}
                      onChange={(e) => setNewQR(prev => ({
                        ...prev,
                        send_sms_on_scan: e.target.checked
                      }))}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings.send_sms_notification', 'Send SMS notification')}</span>
                  </label>
                </div>
              </div>
              )}
            </div>

            {/* Fixed Footer */}
            <div className="p-6 pt-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                {t('settings.cancel', 'Cancel')}
              </button>
              <button
                onClick={createQRCode}
                disabled={!newQR.name || creating || (newQR.code_type === 'public_form' && !newQR.public_form_token_id)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Create'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default QRCodeManagement;
